import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Dimensions, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import CarouselPagination from '@/components/CarouselPagination';
import { Svg, Path, Defs, RadialGradient, Stop, Ellipse } from 'react-native-svg';
import { useRouter } from 'expo-router';
import { useSobriety } from '@/contexts/SobrietyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  subDays, 
  differenceInYears, 
  differenceInMonths, 
  differenceInDays, 
  addYears,
  addMonths,
  getDaysInMonth
} from 'date-fns';
import { calculateSobrietyDays } from '@/utils/sobrietyDays';

interface SobrietyClockCardProps {}


interface TimeBreakdown {
  years: number;
  months: number;
  days: number;
  hours: number;
  daysInCurrentMonth: number;
  totalCompletedDays: number;
}

function calculateTimeBreakdown(startDate: Date, baseDays: number): TimeBreakdown {
  const now = new Date();
  const daysInCurrentMonth = getDaysInMonth(now);
  
  const effectiveStart = subDays(startDate, baseDays);
  
  if (now < effectiveStart) {
    return { years: 0, months: 0, days: 0, hours: 0, daysInCurrentMonth, totalCompletedDays: 0 };
  }
  
  let cursor = effectiveStart;
  
  const years = differenceInYears(now, cursor);
  cursor = addYears(cursor, years);
  
  const months = differenceInMonths(now, cursor);
  cursor = addMonths(cursor, months);
  
  const days = differenceInDays(now, cursor);
  
  // Calculate hours elapsed since the start time, not the current time of day
  // Use the original start time's hour/minute as the reference point
  const totalMilliseconds = now.getTime() - effectiveStart.getTime();
  const totalHours = Math.floor(totalMilliseconds / (1000 * 60 * 60));
  const remainingMinutes = Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const hours = (totalHours + remainingMinutes / 60) % 24; // Fractional hours within the current day cycle
  // Day count comes from the single source of truth so it matches every other
  // surface (Missões, widget, share, Bill) — see utils/sobrietyDays.ts.
  const totalCompletedDays = calculateSobrietyDays(startDate, baseDays, now);
  
  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
    hours: Math.max(0, hours),
    daysInCurrentMonth,
    totalCompletedDays,
  };
}

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_PADDING = 24;
const GALLERY_WIDTH = SCREEN_WIDTH - (CARD_PADDING * 2);

export default function SobrietyClockCard({}: SobrietyClockCardProps) {
  const router = useRouter();
  const { sobrietyState } = useSobriety();
  const { t, language } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);

  const isSetup = sobrietyState.isSetup;

  const formatTodayDate = () => {
    const now = new Date();
    const day = now.getDate();
    const localeMap: Record<string, string> = {
      pt: 'pt-BR',
      en: 'en-US',
      es: 'es-ES'
    };
    const locale = localeMap[language] || 'pt-BR';
    const month = now.toLocaleDateString(locale, { month: 'long' });
    
    if (language === 'en') {
      return t('sobriety.card.todayLabel', { month, day: day.toString() });
    } else {
      return t('sobriety.card.todayLabel', { day: day.toString(), month });
    }
  };

  const timeBreakdown = useMemo(() => {
    if (!sobrietyState.currentRecord) {
      return { years: 0, months: 0, days: 0, hours: 0, daysInCurrentMonth: 31, totalCompletedDays: 0 };
    }
    
    const startDate = new Date(sobrietyState.currentRecord.start_date);
    const baseDays = sobrietyState.currentRecord.base_days || 0;
    
    return calculateTimeBreakdown(startDate, baseDays);
  }, [sobrietyState.currentRecord, currentTime]);

  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(new Date());
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkForNewDay = () => {
      const now = new Date();
      const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0).getTime() - now.getTime();
      
      const timer = setTimeout(() => {
        setCurrentTime(new Date());
        checkForNewDay();
      }, msUntilMidnight);

      return timer;
    };

    const timer = checkForNewDay();
    return () => clearTimeout(timer);
  }, []);

  const handleCardPress = () => {
    router.push('/sobriety-clock');
  };

  const handleShare = () => {
    router.push('/share-sobriety');
  };

  const handleRestart = () => {
    router.push('/sobriety-clock');
  };

  const handleEdit = () => {
    router.push('/sobriety-clock');
  };

  const handleAddWidget = () => {
    router.push('/widget-guide');
  };

  const getTimeLabel = (value: number, singular: string, plural: string) => {
    return value === 1 ? singular : plural;
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const slideIndex = Math.round(event.nativeEvent.contentOffset.x / GALLERY_WIDTH);
    setActiveSlide(slideIndex);
  };

  const calculateProgress = (value: number, max: number) => {
    return Math.min(1, value / max);
  };

  const yearsProgress = calculateProgress(timeBreakdown.years, 10);
  const monthsProgress = calculateProgress(timeBreakdown.months, 12);
  const daysProgress = calculateProgress(timeBreakdown.days, timeBreakdown.daysInCurrentMonth);
  const hoursProgress = calculateProgress(timeBreakdown.hours, 24);

  if (!isSetup) {
    return (
      <TouchableOpacity style={styles.card} onPress={handleCardPress} activeOpacity={0.8}>
        <View style={styles.cardHeader}>
          <View style={styles.cardIcon}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M16.834 2.75018H8.165C5.144 2.75018 3.25 4.88918 3.25 7.91618V16.0842C3.25 19.1112 5.134 21.2502 8.165 21.2502H16.833C19.864 21.2502 21.75 19.1112 21.75 16.0842V7.91618C21.75 4.88918 19.864 2.75018 16.834 2.75018Z" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M15.891 14.0177L12.5 11.9947V7.63367" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
          </View>
          <Text style={styles.setupCardTitle}>{t('sobriety.card.title')}</Text>
        </View>
        <Text style={styles.setupCardDescription}>
          {t('sobriety.card.setupDescription')}
        </Text>
      </TouchableOpacity>
    );
  }

  const ProgressBarsSlide = () => (
    <View style={styles.slideContainer}>
      <View style={styles.progressBarsContainer}>
        <View style={styles.progressBarCard}>
          <View style={[styles.progressBarFill, { width: `${yearsProgress * 100}%` }]} />
          <Text style={styles.progressBarText}>
            {timeBreakdown.years} {getTimeLabel(timeBreakdown.years, t('sobriety.card.year'), t('sobriety.card.years'))}
          </Text>
        </View>

        <View style={styles.progressBarCard}>
          <View style={[styles.progressBarFill, { width: `${monthsProgress * 100}%` }]} />
          <Text style={styles.progressBarText}>
            {timeBreakdown.months} {getTimeLabel(timeBreakdown.months, t('sobriety.card.month'), t('sobriety.card.months'))}
          </Text>
        </View>

        <View style={styles.progressBarCard}>
          <View style={[styles.progressBarFill, { width: `${daysProgress * 100}%` }]} />
          <Text style={styles.progressBarText}>
            {timeBreakdown.days} {getTimeLabel(timeBreakdown.days, t('sobriety.card.dayLabel'), t('sobriety.card.daysLabel'))}
          </Text>
        </View>

        <View style={styles.hoursProgressBarCard}>
          <View style={[styles.hoursProgressBarFill, { width: `${hoursProgress * 100}%` }]}>
            <Svg width="100%" height="100%" viewBox="0 0 300 44" preserveAspectRatio="none" style={styles.hoursSvgDecoration}>
              <Defs>
                <RadialGradient id="whiteBlurHours" cx="50%" cy="50%" rx="50%" ry="50%">
                  <Stop offset="0%" stopColor="white" stopOpacity={0.4} />
                  <Stop offset="100%" stopColor="white" stopOpacity={0} />
                </RadialGradient>
                <RadialGradient id="blueBlurHours" cx="50%" cy="50%" rx="50%" ry="50%">
                  <Stop offset="0%" stopColor="#015480" stopOpacity={0.6} />
                  <Stop offset="100%" stopColor="#015480" stopOpacity={0} />
                </RadialGradient>
              </Defs>
              <Ellipse cx="30" cy="22" rx="40" ry="50" fill="url(#whiteBlurHours)" />
              <Ellipse cx="150" cy="22" rx="60" ry="60" fill="url(#blueBlurHours)" />
              <Ellipse cx="270" cy="22" rx="50" ry="55" fill="url(#blueBlurHours)" />
            </Svg>
          </View>
          <Text style={[
            styles.hoursProgressBarText,
            { color: hoursProgress >= 0.25 ? '#FFFFFF' : '#151515' }
          ]}>
            {Math.floor(timeBreakdown.hours)} {getTimeLabel(Math.floor(timeBreakdown.hours), t('sobriety.card.hour'), t('sobriety.card.hours'))}
          </Text>
        </View>
      </View>
    </View>
  );

  const TotalDaysSlide = () => {
    const prefix = t('sobriety.card.totalDaysPrefix');
    const suffix = t('sobriety.card.totalDaysSuffix');
    const daysWord = t('sobriety.card.totalDaysWord');
    const daysText = `${timeBreakdown.totalCompletedDays} ${daysWord}`;

    return (
      <View style={styles.totalDaysSlide}>
        <View style={styles.totalDaysBackground}>
          <Svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice">
            <Defs>
              <RadialGradient id="whiteBlurTotal" cx="50%" cy="50%" rx="50%" ry="50%">
                <Stop offset="0%" stopColor="white" stopOpacity={0.4} />
                <Stop offset="100%" stopColor="white" stopOpacity={0} />
              </RadialGradient>
              <RadialGradient id="blueBlurTotal1" cx="50%" cy="50%" rx="50%" ry="50%">
                <Stop offset="0%" stopColor="#015480" stopOpacity={0.7} />
                <Stop offset="100%" stopColor="#015480" stopOpacity={0} />
              </RadialGradient>
              <RadialGradient id="blueBlurTotal2" cx="50%" cy="50%" rx="50%" ry="50%">
                <Stop offset="0%" stopColor="#015480" stopOpacity={0.6} />
                <Stop offset="100%" stopColor="#015480" stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Ellipse cx="50" cy="80" rx="60" ry="80" fill="url(#whiteBlurTotal)" />
            <Ellipse cx="200" cy="150" rx="100" ry="120" fill="url(#blueBlurTotal1)" />
            <Ellipse cx="350" cy="100" rx="90" ry="110" fill="url(#blueBlurTotal2)" />
          </Svg>
        </View>
        <Text style={styles.totalDaysText}>
          <Text style={styles.totalDaysTextRegular}>{prefix}</Text>
          <Text style={styles.totalDaysTextBold}>{daysText}</Text>
          <Text style={styles.totalDaysTextRegular}>{suffix}</Text>
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.sectionContainer}>
      <View style={styles.timeHeader}>
        <Text style={styles.todayLabel}>{formatTodayDate()}</Text>
        <Text style={styles.hoursLabelBold}>{t('sobriety.card.hoursLabel')}</Text>
      </View>

      <ScrollView
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        style={styles.gallery}
        contentContainerStyle={styles.galleryContent}
      >
        <View style={[styles.gallerySlide, { width: GALLERY_WIDTH }]}>
          <ProgressBarsSlide />
        </View>
        <View style={[styles.gallerySlide, { width: GALLERY_WIDTH }]}>
          <TotalDaysSlide />
        </View>
      </ScrollView>

      <CarouselPagination count={2} activeIndex={activeSlide} />

      <View style={styles.actionsContainer}>
        <TouchableOpacity style={styles.actionButtonDark} onPress={handleShare} activeOpacity={0.8}>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M8.89173 6.73389L6.59375 9.03187" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path fillRule="evenodd" clipRule="evenodd" d="M6.59544 9.03184L2.0521 6.25384C1.45677 5.88984 1.57744 4.99184 2.2481 4.79784L12.9748 1.69917C13.5848 1.5225 14.1488 2.09184 13.9661 2.69984L10.7834 13.3425C10.5841 14.0092 9.6901 14.1238 9.32877 13.5292L6.59544 9.03184Z" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
          <Text style={styles.actionButtonDarkText}>{t('sobriety.card.share')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.actionButtonLight} onPress={handleRestart} activeOpacity={0.8}>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M9.82627 9.27716L7.96094 8.1629V5.75537" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M3.18555 4.42266C3.46573 4.04066 3.79067 3.69431 4.15322 3.38818" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M11.7082 12.7241C10.804 13.4331 9.68783 13.8909 8.45616 13.9831C7.84976 14.0285 7.25763 13.9805 6.69336 13.854" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M4.56763 12.9072C3.66351 12.282 2.94034 11.4116 2.48828 10.3855" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M2.21907 6.36475C2.04395 7.00683 1.96807 7.68589 2.01996 8.38703" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M13.095 11.149C13.7014 10.1703 14.0393 9.01264 13.992 7.77577C13.8642 4.4609 11.0747 1.87762 7.75977 2.00474" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
          <Text style={styles.actionButtonLightText}>{t('sobriety.card.restart')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.actionButtonLight} onPress={handleEdit} activeOpacity={0.8}>
          <Svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
          <Text style={styles.actionButtonLightText}>{t('sobriety.card.edit')}</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={styles.widgetButton} onPress={handleAddWidget} activeOpacity={0.8}>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path d="M2 6h5V2H2v4zm0 7h5V9H2v4zm7 0h5V9H9v4zM9 2v4h5V2H9z" fill="#413E38"/>
          <Path d="M13 4h-1V3a1 1 0 00-2 0v1h-1a1 1 0 000 2h1v1a1 1 0 002 0V6h1a1 1 0 000-2z" fill="#151515"/>
        </Svg>
        <Text style={styles.widgetButtonText}>{t('sobriety.card.addWidget')}</Text>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path d="M6 4l4 4-4 4" stroke="#807E79" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </Svg>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  sectionContainer: {
    width: '100%',
    gap: 16,
    flexDirection: 'column',
  },
  card: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
    width: '100%',
    height: 164,
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardIcon: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  setupCardTitle: {
    flex: 1,
    fontSize: 16,
    color: '#151515',
    fontFamily: 'Inter_600SemiBold',
  },
  setupCardDescription: {
    fontSize: 20,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  gallery: {
    width: '100%',
  },
  galleryContent: {
    flexDirection: 'row',
  },
  gallerySlide: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  slideContainer: {
    width: '100%',
    gap: 16,
  },
  timeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  todayLabel: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  hoursLabelBold: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
  },
  progressBarsContainer: {
    width: '100%',
    gap: 4,
  },
  progressBarCard: {
    width: '100%',
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    paddingHorizontal: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  progressBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 12,
    backgroundColor: '#DAD8CF',
  },
  progressBarText: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    zIndex: 1,
  },
  hoursProgressBarCard: {
    width: '100%',
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    paddingHorizontal: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  hoursProgressBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 12,
    backgroundColor: '#669AB7',
    overflow: 'hidden',
  },
  hoursDecorativeBackground: {
    position: 'absolute',
    left: 0,
    top: -30,
    right: 0,
    bottom: -30,
    overflow: 'visible',
  },
  hoursSvgDecoration: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  hoursProgressBarText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    zIndex: 1,
  },
  totalDaysSlide: {
    width: '100%',
    height: 188,
    borderRadius: 12,
    backgroundColor: '#669AB7',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  totalDaysBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  totalDaysText: {
    color: '#FFFFFF',
    fontSize: 24,
    lineHeight: 28.8,
    textAlign: 'center',
    paddingHorizontal: 24,
    zIndex: 1,
  },
  totalDaysTextRegular: {
    fontFamily: 'Sentient-Regular',
  },
  totalDaysTextBold: {
    fontFamily: 'Sentient-Bold',
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  actionButtonDark: {
    flex: 1,
    height: 70,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#151515',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  actionButtonDarkText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    lineHeight: 12,
  },
  actionButtonLight: {
    flex: 1,
    height: 70,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  actionButtonLightText: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    lineHeight: 12,
  },
  widgetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    width: '100%',
  },
  widgetButtonText: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
});
